export enum AgreementStateEnum {
  ViewOnly = 'viewonly',
  Overage = 'overage',
  Unlimited = 'unlimited',
  Limited = 'limited',
  Unknown = 'unknown',
}

export enum AgreementKindEnum {
  PA = 'pa',
  EA = 'ea',
  ED = 'ed',
  IP = 'ip',
  RF = 'rf',
}

type AgreementBase = Readonly<{
  id: number;
  // Depends only on the user: this agreement's download allowance is used up.
  // Deliberately not "unavailable" -- an agreement can also fail to apply to
  // the search currently being run, which is a fact about the search and is
  // derived client-side by `isAgreementApplicable`. Folding the two into one
  // flag is what forced the agreements resource to be search-scoped.
  exhausted: boolean;
}>;

// AgreementDTO is the plain wire shape — the agreement payload as the BFF
// serializes it. It is the factory input and the BFF response type; React
// components do not consume it directly, they use the enriched `Agreement`
// below. `kind` is the discriminant; consumers narrow on it.
export type PaAgreementDTO = AgreementBase &
  Readonly<{
    kind: AgreementKindEnum.PA;
    name: string;
    state: AgreementStateEnum;
    downloadCount?: number;
    isClientLicense?: boolean;
  }>;

export type EaAgreementDTO = AgreementBase &
  Readonly<{
    kind: AgreementKindEnum.EA;
    hasCompAgreement: boolean;
  }>;

export type EdAgreementDTO = AgreementBase &
  Readonly<{
    kind: AgreementKindEnum.ED;
  }>;

export type IpAgreementDTO = AgreementBase &
  Readonly<{
    kind: AgreementKindEnum.IP;
  }>;

export type RfSubscriptionType = 'creative' | 'editorial';

export type RfAgreementDTO = AgreementBase &
  Readonly<{
    kind: AgreementKindEnum.RF;
    downloadCount?: number;
    subscriptionType?: RfSubscriptionType;
  }>;

export type AgreementDTO =
  | PaAgreementDTO
  | EaAgreementDTO
  | EdAgreementDTO
  | IpAgreementDTO
  | RfAgreementDTO;

/**
 * The token that identifies an agreement in a search: `kind:id`, e.g. `pa:42`.
 *
 * The two functions below are the only code that knows that shape. It was
 * previously spelled out wherever it was needed -- built here, parsed with
 * `split(':')` in the service, and compared against the literal `'pa:'` in
 * several places -- so a change to the format had no single place to happen.
 */
export const filterValueFor = (kind: AgreementKindEnum, id: number): string =>
  `${kind}:${id}`;

/** The id a filter value names, or `undefined` when it names none. */
export const agreementIdIn = (filterValue: string): number | undefined => {
  const id = Number(filterValue.split(':')[1]);

  return Number.isInteger(id) && id > 0 ? id : undefined;
};

export interface LabelDescriptor {
  key: string;
  params?: Record<string, string | undefined>;
}

// Helpers shared by every kind.
export interface BaseAgreementHelpers {
  readonly filterValue: string;
  isExhausted(): boolean;
}

// PA only: state-driven labels.
export interface PaAgreementHelpers {
  readonly stateLabel: LabelDescriptor;
  readonly subLabelParts: LabelDescriptor[];
}

// EA only: comp-agreement predicate.
export interface EaAgreementHelpers {
  hasComp(): boolean;
}

// Agreement is the enriched, React-facing representation: the wire shape plus
// the helpers that apply to its kind. Built only via AgreementFactory; the
// discriminant is still `kind`, so a consumer that narrows on it sees only the
// helpers valid for that kind.
export type PaAgreement = PaAgreementDTO &
  BaseAgreementHelpers &
  PaAgreementHelpers;
export type EaAgreement = EaAgreementDTO &
  BaseAgreementHelpers &
  EaAgreementHelpers;
export type RfAgreement = RfAgreementDTO & BaseAgreementHelpers;
export type EdAgreement = EdAgreementDTO & BaseAgreementHelpers;
export type IpAgreement = IpAgreementDTO & BaseAgreementHelpers;

export type Agreement =
  | PaAgreement
  | EaAgreement
  | RfAgreement
  | EdAgreement
  | IpAgreement;

// "N downloads remaining" — shared by the PA Limited state label and the
// Overage sub-label part.
const downloadsRemainingLabel = (downloadCount?: number): LabelDescriptor => ({
  key: 'search_filters.agreement_downloads_remaining',
  params: { count: downloadCount?.toString() },
});

// ClientLicense agreements show "Unlimited" rather than their finite quota — but
// only for the quota-derived states that would otherwise expose a number.
// ViewOnly (no download permission) and Unknown (no usable quota) keep their own labels.
const showsUnlimited = (
  dto: PaAgreementDTO,
  agencyUnlimited: boolean
): boolean =>
  Boolean(agencyUnlimited && dto.isClientLicense) &&
  (dto.state === AgreementStateEnum.Limited ||
    dto.state === AgreementStateEnum.Overage);

// PA state → ilox label descriptor. Domain owns the mapping; React consumers
// translate the descriptor into a string at render time.
const paStateLabel = (
  dto: PaAgreementDTO,
  agencyUnlimited: boolean
): LabelDescriptor => {
  if (showsUnlimited(dto, agencyUnlimited)) {
    return { key: 'unlimited_titlecase' };
  }

  switch (dto.state) {
    case AgreementStateEnum.Overage:
      return { key: 'overage_titlecase' };
    case AgreementStateEnum.Unlimited:
      return { key: 'unlimited_titlecase' };
    case AgreementStateEnum.ViewOnly:
      return { key: 'view_only' };
    case AgreementStateEnum.Limited:
      return downloadsRemainingLabel(dto.downloadCount);
    case AgreementStateEnum.Unknown:
      // No translated label for Unknown; a blank descriptor renders nothing.
      return { key: '' };
    default: {
      // Exhaustiveness guard: a new AgreementStateEnum member breaks the build.
      const exhaustive: never = dto.state;
      return exhaustive;
    }
  }
};

// Composite filter sub-label: typically one entry; Overage adds a downloads
// remaining entry alongside the state label.
const paSubLabelParts = (
  dto: PaAgreementDTO,
  agencyUnlimited: boolean
): LabelDescriptor[] => {
  const parts: LabelDescriptor[] = [paStateLabel(dto, agencyUnlimited)];
  if (
    !showsUnlimited(dto, agencyUnlimited) &&
    dto.state === AgreementStateEnum.Overage &&
    dto.downloadCount !== undefined
  ) {
    parts.push(downloadsRemainingLabel(dto.downloadCount));
  }
  return parts;
};

const baseHelpers = (dto: AgreementDTO): BaseAgreementHelpers => ({
  filterValue: filterValueFor(dto.kind, dto.id),
  isExhausted: () => dto.exhausted,
});

const enrichPa = (
  dto: PaAgreementDTO,
  agencyUnlimited: boolean
): PaAgreement => ({
  ...dto,
  ...baseHelpers(dto),
  stateLabel: paStateLabel(dto, agencyUnlimited),
  subLabelParts: paSubLabelParts(dto, agencyUnlimited),
});

const enrichEa = (dto: EaAgreementDTO): EaAgreement => ({
  ...dto,
  ...baseHelpers(dto),
  hasComp: () => dto.hasCompAgreement,
});

export class AgreementFactory {
  // Merges the wire DTO with its helper closures into a single object literal,
  // switching on `kind`. The result is the enriched `Agreement` the rest of the
  // app consumes — `kind` keeps the union narrowable.
  fromDTO = (dto: AgreementDTO, agencyUnlimited = false): Agreement => {
    switch (dto.kind) {
      case AgreementKindEnum.PA:
        return enrichPa(dto, agencyUnlimited);
      case AgreementKindEnum.EA:
        return enrichEa(dto);
      case AgreementKindEnum.RF:
        return { ...dto, ...baseHelpers(dto) } satisfies RfAgreement;
      case AgreementKindEnum.ED:
        return { ...dto, ...baseHelpers(dto) } satisfies EdAgreement;
      case AgreementKindEnum.IP:
        return { ...dto, ...baseHelpers(dto) } satisfies IpAgreement;
      default: {
        // Exhaustiveness guard: a new AgreementKindEnum member breaks the build.
        const exhaustive: never = dto;
        return exhaustive;
      }
    }
  };
}
